import { Benevole, BenevoleDetail, ElementKey, HabilitationCategory, PiwigoLevel } from "@/models";
import { toast } from "@/store";
import { TYPE } from "@/store/modules/toast.module";
import { Http } from "@/utils/http";

export default class BenevoleService {
  public static async benevoles(): Promise<Benevole[]> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevoles`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data;
  }

  public static async benevole(id: string): Promise<BenevoleDetail> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevole&id=${id}`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data;
  }

  public static async habilitations(userId: string): Promise<HabilitationCategory[]> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevoleHabilitations&id=${userId}`;
    const { data: response } = await Http.instance.get(targetUrl);
    return response.data ?? [];
  }

  public static async toggleHabilitation(
    userId: string,
    habilitationId: string,
    active: boolean
  ): Promise<void> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevoleHabilitationUpdate`;
    await Http.addPostSettings();
    await Http.instance.post(
      targetUrl,
      Http.qsStringify({ user_id: userId, habilitation_id: habilitationId, active: active ? 1 : 0 })
    );
  }

  public static async updateCompetence(
    userId: string,
    competenceId: string,
    stepId: string,
    commentaire: string | null
  ): Promise<void> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevoleUpdateCompetence`;
    await Http.instance.post(
      targetUrl,
      { user: userId, competence: competenceId, step: stepId, commentaire },
      { headers: { "Content-Type": "application/json" } }
    );
  }

  public static async canAddFromSlack(): Promise<
    { id: string; username: string; nom_usage: string; picture: string; telephone: string | null }[]
  > {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevoleCanAddFromSlack&q=`;
    const { data: response } = await Http.instance.get(targetUrl);
    if (!response.data) return [];
    return Object.entries(response.data)
      .filter(([key]) => !isNaN(Number(key)))
      .map(([, value]) => value as { id: string; username: string; nom_usage: string; picture: string; telephone: string | null });
  }

  public static async create(benevole: {
    prenom: string;
    nom: string;
    nom_usage: string;
    username: string;
    slack_id: string;
    telephone: string;
  }): Promise<string | null> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevoleCreate`;
    await Http.addPostSettings();
    const { data: response } = await Http.instance.post(
      targetUrl,
      Http.qsStringify({ benevole })
    );
    if (response.error) {
      toast.pushError(response.message ?? "Une erreur s'est produite.");
      return null;
    }
    if (response.success) {
      toast.createAndPushToast(
        response.message ?? "Bénévole créé",
        "",
        true,
        TYPE.SUCCESS
      );
    }
    return response.data?.id ?? null;
  }

  public static async piwigoGetAccess(userId: string): Promise<PiwigoLevel> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=piwigoGetAccess&id=${userId}`;
    const { data: response } = await Http.instance.get(targetUrl);
    return (response.data?.level as PiwigoLevel) ?? "NONE";
  }

  public static async piwigoSetAccess(userId: string, level: PiwigoLevel): Promise<void> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=piwigoSetAccess`;
    await Http.addPostSettings();
    await Http.instance.post(targetUrl, Http.qsStringify({ user_id: userId, level }));
  }

  public static async update(benevole: Benevole): Promise<ElementKey> {
    const targetUrl = `${process.env.VUE_APP_API_APP}/?task=benevoleUpdate`;
    await Http.addPostSettings();
    const { data: response } = await Http.instance.post(
      targetUrl,
      Http.qsStringify({
        benevole,
      })
    );
    if (response.error) {
      toast.pushError(response.message ?? "Une erreur s'est produite.");
    }
    if (response.success) {
      toast.createAndPushToast(
        response.message ?? "OK",
        "",
        true,
        TYPE.SUCCESS
      );
    }
    return response.data?.id;
  }
}
